import {
  WSList,
  WSModal,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  AttributeDataType,
  AttributeDefinition,
  WorkDefinition
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { DataView } from "../../../../shared/components/DataView";

export interface ModalWorkDefinitionDetailsProps {
  workDefinition: WorkDefinition;
  onClose: () => void;
}

const getFieldTypeLabel = (type: AttributeDataType): string => {
  const labels: Record<AttributeDataType, string> = {
    [AttributeDataType.BOOLEAN]: "Yes/No",
    [AttributeDataType.STRING]: "Text",
    [AttributeDataType.NUMBER]: "Number",
    [AttributeDataType.DATETIME]: "Date/time",
    [AttributeDataType.VALUE_SET]: "Selection"
  };
  return labels[type] || type;
};

const getValidationText = (field: AttributeDefinition): string => {
  const rules = field.validationRules;
  if (!rules) return "None";

  const parts: string[] = [];

  if (rules.minimum !== undefined) {
    parts.push(`Min: ${rules.minimum}`);
  }
  if (rules.maximum !== undefined) {
    parts.push(`Max: ${rules.maximum}`);
  }
  if (rules.regex) {
    parts.push(`Pattern: ${rules.regex}`);
  }
  if (rules.enumConfig) {
    if (
      "acceptedValues" in rules.enumConfig &&
      rules.enumConfig.acceptedValues
    ) {
      parts.push(`Options: ${rules.enumConfig.acceptedValues.length}`);
    } else if ("resourceType" in rules.enumConfig) {
      parts.push("Dynamic options");
    }
  }

  return parts.length > 0 ? parts.join(", ") : "None";
};

export const ModalWorkDefinitionDetails: React.FC<
  ModalWorkDefinitionDetailsProps
> = ({ workDefinition, onClose }) => {
  const tableData = useMemo(() => {
    return (workDefinition.attributeDefinitions ?? []).map((field, index) => ({
      id: field.key || `field-${index}`,
      data: field
    }));
  }, [workDefinition.attributeDefinitions]);

  const columns: Array<WSTableColumn<AttributeDefinition>> = [
    {
      config: {
        header: {
          text: "Label"
        }
      },
      renderFunction: rowItem => <WSTableCell text={rowItem.data.name} />
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: rowItem => (
        <WSTableCell text={getFieldTypeLabel(rowItem.data.type)} />
      )
    },
    {
      config: {
        header: {
          text: "Required"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: rowItem =>
        rowItem.data.isRequired ? (
          <WSTableCell text="Yes" />
        ) : (
          <WSTableCell text="No" secondaryText="Optional" />
        )
    },
    {
      config: {
        header: {
          text: "Validation"
        },
        hideOnScreens: ["XS", "S"]
      },
      renderFunction: rowItem => (
        <WSTableCell text={getValidationText(rowItem.data)} />
      )
    }
  ];

  return (
    <WSModal
      title="Work type details"
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            onClick: onClose,
            label: "Done"
          }
        ]
      }}
      size="M"
      onClose={onClose}
    >
      <WSList gap="L">
        <DataView
          data={[
            {
              label: "Work type",
              value: workDefinition.name
            }
          ]}
        />

        <WSText.ParagraphSm weight="medium" color="gray600">
          Field configuration
        </WSText.ParagraphSm>

        {tableData.length > 0 ? (
          <WSTable<AttributeDefinition>
            tableData={tableData}
            columns={columns}
            data-testid="workDefinitionFieldsTable"
          />
        ) : (
          <WSText.ParagraphSm color="gray500">
            No fields configured
          </WSText.ParagraphSm>
        )}
      </WSList>
    </WSModal>
  );
};
